import { flashNotices } from 'constants/flash-notices'
import useDebounce from 'hooks/use-debounce'
import useWindowSize from 'hooks/use-window-size'
import { useRouter } from 'next/router'
import Pusher from 'pusher-js'
import {
  Dispatch,
  FC,
  ReactElement,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import type Env from 'types/env'
import Notice from 'types/flash-notice'
import Notification from 'types/notification'
import { ModalProps } from 'ui'

interface Interface {
  darkMode: boolean
  toggleDarkMode: () => void
  navigationCollapsed: boolean
  toggleNavigation: () => void
  mobileNavigationClosed: boolean
  toggleMobileNavigation: () => void
  modal: ReactElement
  setModal: Dispatch<ReactElement>
  modalProps: Partial<ModalProps>
  setModalProps: Dispatch<Partial<ModalProps>>
  resetModalProps: () => void
  flashNotice: Notice
  setFlashNotice: Dispatch<SetStateAction<Notice>>
  notification: Notification
  setNotification: Dispatch<SetStateAction<Notification>>
  fileCache: Map<string, { accessed: number; data: any }>
  navigationHidden: boolean
  setNavigationHidden: Dispatch<boolean>
  authProvider: 'credentials' | 'sso'
  setLastAuthProvider: (lastAuthProvider: 'credentials' | 'sso') => void
  pusherClient: Pusher
  env: Env
}

const AppContext = createContext<Partial<Interface>>({
  darkMode: false,
  navigationCollapsed: false,
  mobileNavigationClosed: true,
  modal: null,
  navigationHidden: false,
})

interface Props {
  children: ReactElement
}

const AppProvider: FC<Props> = ({ children }) => {
  const fileCache = useRef(new Map())
  const { pathname, query } = useRouter()
  const [darkMode, setDarkMode] = useState<boolean>(false)
  const [authProvider, setAuthProvider] = useState<'credentials' | 'sso'>(
    'credentials',
  )
  const [navigationCollapsed, setNavigationCollapsed] = useState<boolean>(false)
  const [navigationHidden, setNavigationHidden] = useState<boolean>(false)
  const [mobileNavigationClosed, setMobileNavigationClosed] =
    useState<boolean>(true)
  const [modal, setModal] = useState<ReactElement>(null)
  const [modalProps, setModalProps] = useState<Partial<ModalProps>>({})
  const [flashNotice, setFlashNotice] = useState<Notice>(() => {
    const notice = flashNotices[String(query.setFlashNotice)]
    return notice ? { type: notice.type, message: notice.message } : null
  })
  const [flashNoticeSeen, setFlashNoticeSeen] = useState(false)
  const [notification, setNotification] = useState<Notification | null>(null)
  const [pusherClient, setPusherClient] = useState(null)
  const [env, setEnv] = useState<Env>(null)

  const controller = new AbortController()
  const signal = controller.signal

  const setEnvironment = useCallback(
    (data: Env) => {
      if (signal.aborted) {
        return
      }

      setEnv(data as Env)
    },
    [signal],
  )

  const getEnvironment = async () => {
    try {
      const response = await fetch(`/api/env`, {
        method: 'GET',
        signal,
      })
      const data = await response.json()
      setEnvironment(data)
    } catch (e) {}
  }

  useEffect(() => {
    getEnvironment()

    return () => {
      controller.abort()
    }
  }, [])

  useEffect(() => {
    if (env && !pusherClient) {
      setPusherClient(
        new Pusher(env.PUSHER_ACCESS_KEY, {
          cluster: env.PUSHER_CLUSTER,
        }),
      )
    }
  }, [env, pusherClient])

  const router = useRouter()
  const previousPath = useRef('')

  const resetModalProps = () => {
    setModalProps({})
  }

  const { width } = useWindowSize()
  useDebounce(
    () => {
      if (flashNotice?.type === 'success') {
        setFlashNotice(null)
        setFlashNoticeSeen(false)
      }
    },
    [flashNotice],
    7000,
  )

  const toggleDarkMode = useCallback(() => {
    localStorage.setItem('dark-mode', JSON.stringify(!darkMode))
    setDarkMode(!darkMode)
  }, [darkMode])

  const setLastAuthProvider = (lastAuthProvider: 'credentials' | 'sso') => {
    localStorage.setItem('last-auth-provider', lastAuthProvider)
    setAuthProvider(lastAuthProvider)
  }

  useEffect(() => {
    if (!mobileNavigationClosed && width > 768) {
      setMobileNavigationClosed(true)
    }
  }, [width])

  // when the route changes, if a flash notice exists,
  // mark it as seen. on the next route change, clear it.
  useEffect(() => {
    if (flashNotice) {
      flashNoticeSeen === true ? setFlashNotice(null) : setFlashNoticeSeen(true)
    }
  }, [pathname])

  useEffect(() => {
    if (!flashNotice) {
      setFlashNoticeSeen(false)
    }
  }, [flashNotice])

  useEffect(() => {
    const notice = flashNotices[String(query.setFlashNotice)]
    if (notice) {
      setFlashNotice({ type: notice.type, message: notice.message })
    }
  }, [query])

  useEffect(() => {
    setMobileNavigationClosed(true)
  }, [pathname])

  useEffect(() => {
    setAuthProvider(
      localStorage.getItem('last-auth-provider') === 'sso'
        ? 'sso'
        : 'credentials',
    )
  }, [authProvider])

  useEffect(() => {
    setDarkMode(localStorage.getItem('dark-mode') === 'true')
  }, [darkMode])

  useEffect(() => {
    const currentPath = router.asPath
    const partsCollapse =
      currentPath?.includes('/parts/') &&
      !previousPath.current.includes('/parts/')
    const cutConfigCollapse =
      currentPath?.includes('/cut-configs/') &&
      !previousPath.current.includes('/cut-configs/')
    // Automatically collapse the sidebar on the parts report tabs and edit cut config page
    const shouldCollapse =
      localStorage.getItem('navigation') === 'collapsed' ||
      partsCollapse ||
      cutConfigCollapse
    if (shouldCollapse) {
      setNavigationCollapsed(true)
    }
    previousPath.current = currentPath
  }, [router.asPath])

  const toggleNavigation = useCallback(() => {
    localStorage.setItem(
      'navigation',
      !navigationCollapsed ? 'collapsed' : 'expanded',
    )
    setNavigationCollapsed(!navigationCollapsed)
  }, [navigationCollapsed])

  const toggleMobileNavigation = useCallback(() => {
    setMobileNavigationClosed(!mobileNavigationClosed)
  }, [mobileNavigationClosed])

  if (!env || !pusherClient) {
    return <></>
  }

  return (
    <AppContext.Provider
      value={{
        darkMode,
        toggleDarkMode,
        navigationCollapsed,
        toggleNavigation,
        mobileNavigationClosed,
        toggleMobileNavigation,
        modal,
        setModal,
        modalProps,
        setModalProps,
        resetModalProps,
        flashNotice,
        setFlashNotice,
        notification,
        setNotification,
        fileCache: fileCache.current,
        navigationHidden,
        setNavigationHidden,
        authProvider,
        setLastAuthProvider,
        pusherClient,
        env,
      }}>
      {children}
    </AppContext.Provider>
  )
}

const AppConsumer = AppContext.Consumer
const useApp = () => useContext(AppContext)

export { AppProvider, AppContext, AppConsumer, useApp }
